import React, { useEffect, useState } from "react";
import Table from "../../../../common/element/Table";
import Button from "../../../../common/element/Buttons";
import ArchiveModal from "../../../../common/Common/ArchiveModal";
import { selectUserData } from "../../../../redux/auth/authSelector";
import { useDispatch, useSelector } from "react-redux";
import { ROLES } from "../../../../config/rolePermissions";
import EmployeeAssignmentDialog from "./EmployeeAssignmentView";
import SweetAlertService from "../../../../common/SweetAlert/SweetAlert";
// Updated imports for Archive instead of Delete
import { archiveAssignments, clearArchiveAssignments } from "../../../../redux/Assignment/assignmentsSlice"; 
import { selectArchiveAssignmentsLoading } from "../../../../redux/Assignment/assignmentsSelector";
import ThreeDotBouncingLoader from "../../../../common/element/ButtonLoader/ThreeDotBouncingLoder";
import CommonIconButton from "../../../../common/icons/CommonIconButton";

const { Tr, Th, Td, THead, TBody } = Table;

const AssignmentsList = ({
  sampleData = [],
  onEdit,
  onView,
  currentPage = 1,
  pageLimit = 10,
  loading,
  handleRefresh,
  isDraftList = false,
}) => {
  const dispatch = useDispatch();
  const userData = useSelector(selectUserData);
  const userRole = userData?.role_name;
  const isAdmin = userRole === ROLES.ADMIN;

  // Archive States
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);
  const [assignmentToArchive, setAssignmentToArchive] = useState(null);
  const archiveLoading = useSelector(selectArchiveAssignmentsLoading);

  const [isEmployeeDialog, setIsEmployeeDialog] = useState(false);
  const [selectedSessionAssignment, setSelectedSessionAssignment] = useState(null);
  const [mode, setMode] = useState("edit");

  // Handle Archive Click
  const handleArchiveClick = (item) => {
    setAssignmentToArchive(item);
    setIsArchiveOpen(true);
  };

  // Handle Confirm Archive/Unarchive
  const handleArchiveConfirm = async () => {
    if (!assignmentToArchive) return;
    
    // Determine ID (handling different data structures)
    const id = assignmentToArchive.activity_id || assignmentToArchive.assignment_id || assignmentToArchive.id;
    const isArchiving = !assignmentToArchive.is_archived;

    try {
      await dispatch(archiveAssignments({
        id: id,
        is_archive: isArchiving
      })).unwrap();

      setIsArchiveOpen(false);
      setAssignmentToArchive(null);
      dispatch(clearArchiveAssignments());

      SweetAlertService.showSuccess({
        title: "Success",
        message: `Assignment ${isArchiving ? 'archived' : 'unarchived'} successfully.`,
        onConfirm: () => {
          handleRefresh?.();
        }
      });
    } catch (error) {
      setIsArchiveOpen(false);
      SweetAlertService.showError({
        title: "Error",
        message: error || "Failed to update assignment status.",
      });
    }
  };

  const getStatusColor = (status, isArchive) => {
    if (isArchive) return "bg-slate-100 text-slate-700"; // Specific style for archived
    switch (status?.toLowerCase()) {
      case "completed": return "text-green-600 bg-green-50 ";
      case "pending": return "text-yellow-600 bg-yellow-50";
      case "upcoming": return "text-blue-600 bg-blue-50";
      case "cancelled": return "text-red-600 bg-red-50";
      default: return "text-gray-600 bg-gray-50";
    }
  };

  const handleOpenAssignment = (item, modeType) => {
    const idToPass = item;
    setSelectedSessionAssignment(idToPass);
    setMode(modeType);
    setIsEmployeeDialog(true);
  };

  return (
    <>
      <div className="border rounded-lg border-gray-200">
        <Table>
          <THead>
            <Tr className="text-start">
              <Th className="text-start">S No.</Th>
              {!isAdmin && <Th className="text-start">Session Name</Th>}
              <Th className="text-start">Name of Assignment</Th>
              <Th className="text-start">Number of Questions</Th>
              {!isAdmin ? <Th className="text-start">Status</Th> : <Th className="text-start">Access For</Th>}
              {isAdmin && <Th className="text-start">Status</Th>} 
              <Th className="text-start">Action</Th>
            </Tr>
          </THead>
          <TBody>
            {loading ? (
               <Tr><Td colSpan={!isAdmin ? 6 : 7} className="text-center py-6"><ThreeDotBouncingLoader/></Td></Tr>
            ) : sampleData.length > 0 ? (
              sampleData.map((item, index) => {
                const uniqueId = item.id || item.activity_id || item.assignment_id;
                const sessionName = !isAdmin ? item.companySession?.session_name : (item.session_name || "N/A");
                const assignmentName = !isAdmin ? item.assignment?.assignment_name : item.assignment_name;
                const questionCount = !isAdmin ? item.assignment?.questions?.length || item.assignment?.question_count || 0 : item.questions?.length || 0;

                return (
                  <Tr key={uniqueId} className="text-start">
                    <Td>{String((currentPage - 1) * pageLimit + (index + 1)).padStart(2, "0")}</Td>
                    {!isAdmin && <Td>{sessionName || "-"}</Td>}
                    <Td>{assignmentName || "-"}</Td>
                    <Td>{questionCount}</Td>
                    
                    {/* Access For / Status Column */}
                    {!isAdmin ? (
                      <Td className="text-start ">
                        <span className={`px-3 py-1 rounded-full text-md font-medium capitalize ${getStatusColor(item.status || "pending", false)}`}>
                          {item.status || "pending"}
                        </span>
                      </Td>
                    ) : (
                       <Td>
                        {item.access_for && item.access_for.length > 0 ? (
                          item.access_for.some(r => r.access_group_name === "All")
                            ? "Management, Employee"
                            : item.access_for
                              .filter(r => r.access_group_name !== "All")
                              .map(r => r.access_group_name)
                              .join(", ") || "N/A"
                        ) : (
                          "N/A"
                        )}
                      </Td>
                    )}

                    {/* Admin Status Column (Active/Archived) */}
                    {isAdmin && (
                      <Td className="text-start">
                        <span
                          className={`inline-block px-3 py-1 text-sm font-medium rounded-full capitalize ${item.is_archived
                              ? "bg-slate-100 text-slate-700"
                              : "bg-green-100 text-green-800"
                            }`}
                        >
                          {item.is_archived ? "Archived" : "Active"}
                        </span>
                      </Td>
                    )}

                    {!isAdmin ? (
                      <Td>
                        <div className="flex gap-2">
                          {isDraftList ? (
                            <Button
                              onClick={() => handleOpenAssignment(item, "edit")}
                              title="View & Edit Assignment"
                              className="px-4 h-[36px] bg-[#FF8A00] text-white text-sm font-medium rounded-md border border-[#FF8A00] flex items-center justify-center hover:bg-[#e67a00] transition-all"
                            >
                              View & Edit Assignment
                            </Button>
                          ) : item.status?.toLowerCase() === "completed" ? (
                            <Button
                              onClick={() => handleOpenAssignment(item, "view")}
                              title="View Answers"
                              className="px-4 h-[36px] bg-[#FF8A00] text-white text-sm font-medium rounded-md border border-[#FF8A00] flex items-center justify-center hover:bg-[#e67a00] transition-all"
                            >
                              View Answers
                            </Button>
                          ) : (
                            <Button
                              onClick={() => handleOpenAssignment(item, "edit")}
                              title="Start Assignment"
                              className="px-4 h-[36px] bg-[#FF8A00] text-white text-sm font-medium rounded-md border border-[#FF8A00] flex items-center justify-center hover:bg-[#e67a00] transition-all"
                            >
                              Start Assignment
                            </Button>
                          )}
                        </div>
                      </Td>
                    ) : (
                      <Td>
                        <div className="flex gap-1">
                          {/* View Button */}
                          <CommonIconButton
                        type="view" onClick={() => onView(item)}/>
                         
                          
                          {/* Edit Button (Only if not archived) */}
                          {!item.is_archived && (
                            <CommonIconButton
                              type="edit"
                              onClick={() => onEdit(item)}
                            />
                          )}

                          {/* Archive/Unarchive Button */}
                          <CommonIconButton
                            type={item.is_archived ? "unarchive" : "archive"}
                            onClick={() => handleArchiveClick(item)}
                         />
                          
                        </div>
                      </Td>
                    )}
                  </Tr>
                );
              })
            ) : (
              <Tr><Td colSpan={!isAdmin ? 6 : 7} className="text-center text-gray-500 py-6 text-sm">No matching records found</Td></Tr>
            )}
          </TBody>
        </Table>

        {/* Updated Archive Modal */}
        <ArchiveModal
          dialogOpen={isArchiveOpen}
          onDialogClose={() => {
            setIsArchiveOpen(false);
            setAssignmentToArchive(null);
          }}
          onConfirm={handleArchiveConfirm}
          loading={archiveLoading}
     
              title={ assignmentToArchive?.is_archived == true 
            ? `Are you sure you want to unarchive "${assignmentToArchive?.assignment_name}" Assignment ?`
            : `Are you sure you want to archive "${assignmentToArchive?.assignment_name}" Assignment`}
        />

        <EmployeeAssignmentDialog 
          dialogOpen={isEmployeeDialog} 
          onDialogClose={() => {
            setIsEmployeeDialog(false);
            setSelectedSessionAssignment(null);
          }}
          sessionAssignmentId={selectedSessionAssignment}
          mode={mode} 
          handleRefresh={handleRefresh}
        />
      </div>
    </>
  );
};

export default AssignmentsList;